<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProject, useCreateProject, useUpdateProject, useDeleteProject, useInvalidateProjects } from "@/queries/projects";
import { useProducts, useSubscribedProducts, useInvalidateProducts } from "@/queries/products";
import { useSessionStore } from "@/stores/session-store";
import { useAlertStore } from "@/stores/alerts-store";
import { storeToRefs } from "pinia";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import ConvertToProductModal from "./modals/ConvertToProductModal.vue";
import DeleteProjectModal from "./modals/DeleteProjectModal.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import BlockUI from "@/components/BlockUI.vue";
import FormAlert from "@/components/FormAlert.vue";
import type { Project } from "@/types";

const route = useRoute();
const router = useRouter();

const sessionStore = useSessionStore();
const { session } = storeToRefs(sessionStore);
const alertStore = useAlertStore();

const projectId = computed(() => route.params.projectid as string);
const isEditMode = computed(() => route.name === "project-edit" || route.query.mode === "edit");
const isNewMode = computed(() => route.name === "project-new" || route.params.projectid === "new");

const { project, projectLoading: loading } = useProject(projectId);
const { products } = useProducts();
const { subscribedProducts } = useSubscribedProducts();
const { createProject, createProjectPending, createProjectError, createProjectReset } = useCreateProject();
const { updateProject, updateProjectPending, updateProjectError, updateProjectReset } = useUpdateProject();
const { deleteProject, deleteProjectPending } = useDeleteProject();
const invalidateProjects = useInvalidateProjects();
const invalidateProducts = useInvalidateProducts();

const saving = computed(() => createProjectPending.value || updateProjectPending.value || deleteProjectPending.value);
const saveError = computed(() => createProjectError.value || updateProjectError.value);

const localProject = ref({} as Project);

const rules = {
  name: { required },
};
const validation$ = useVuelidate(rules, localProject);

// Combine owned and subscribed products for the dropdown
const availableProducts = computed(() => {
  // Create a map to avoid duplicates (in case a product is both owned and subscribed)
  const productsMap = new Map();

  // Add owned products
  products.value.forEach(product => {
    productsMap.set(product.id, { ...product, isOwned: true });
  });

  // Add subscribed products
  subscribedProducts.value.forEach(product => {
    if (!productsMap.has(product.id)) {
      productsMap.set(product.id, { ...product, isOwned: false });
    }
  });

  return Array.from(productsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
});

// The product a project points at lives in `subscriberProductId` for Subscriber projects and
// `publisherProductId` for PublisherMaster projects. The dropdown binds here so the read-only
// edit view resolves whichever field is populated; the setter only writes `publisherProductId`
// since the create flow (the only place the dropdown is editable) produces Subscriber/DIY projects.
const selectedProductId = computed({
  get: () => localProject.value.subscriberProductId || localProject.value.publisherProductId || "",
  set: (value: string) => { localProject.value.publisherProductId = value; },
});

// Convert to Product modal state
const showConvertModal = ref(false);

// Delete confirmation modal state
const showDeleteModal = ref(false);

const isProductlessProject = computed(() => {

  if(project.value?.projectType == "DIY") return true;

  //if subscriberProductId is null or empty and publisherProductId is null or empty, then it's a productless project
  return (!project.value?.subscriberProductId || project.value.subscriberProductId === "") &&
      (!project.value?.publisherProductId || project.value.publisherProductId === "");
});

const isPublisherProject = computed(() => {
  if (!localProject.value.publisherProductId) return false;
  const product = availableProducts.value.find(
    (p) => p.id === localProject.value.publisherProductId
  );
  if (!product) return false;

  return product.masterProjectId === localProject.value.id;
});

const availableVersions = ref([
  {
    id: "1",
    version: "2.4",
    status: "Draft",
    publishedDate: "",
    isActive: false,
  },
  {
    id: "2",
    version: "2.3",
    status: "Draft",
    publishedDate: "",
    isActive: false,
  },
  {
    id: "3",
    version: "2.2",
    status: "Preview",
    publishedDate: "2024-08-10",
    isActive: false,
  },
  {
    id: "4",
    version: "2.1",
    status: "Published",
    publishedDate: "2024-07-25",
    isActive: true,
  },
  {
    id: "5",
    version: "2.0",
    status: "Published",
    publishedDate: "2024-06-15",
    isActive: false,
  },
]);

function loadProject() {
  if (!project.value) return;
  localProject.value = { ...project.value };

  if (!localProject.value.selectedVersion && isPublisherProject.value) {
    const activeVersion = availableVersions.value.find((v) => v.isActive);
    if (activeVersion) {
      localProject.value.selectedVersion = activeVersion.id;
    }
  }
}

function saveProject() {
  validation$.value.$validate().then(valid => {
    if (!valid) return;
    resetErrors();
    updateProject(localProject.value as any, {
      onSuccess: () => router.push("/projects"),
    });
  });
}

function createNewProject() {
  validation$.value.$validate().then(valid => {
    if (!valid) return;
    resetErrors();
    // PublisherMaster projects originate from the product-creation flow — this form only
    // produces Subscriber (product selected) or DIY (no product) projects.
    const productId = localProject.value.publisherProductId;
    createProject(
      {
        name: localProject.value.name,
        projectType: productId ? "Subscriber" : "DIY",
        productId: productId || undefined,
      },
      {
        onSuccess: (newProjectId) => router.push(`/projects/${newProjectId}`),
      }
    );
  });
}

function deleteProjectHandler() {
  showDeleteModal.value = true;
}

function confirmDelete() {
  deleteProject(localProject.value.id, {
    onSuccess: () => {
      showDeleteModal.value = false;
      router.push("/projects");
    },
    // 409 = blocked (in-flight runs / published product / active schedules).
    onError: (e) => {
      showDeleteModal.value = false;
      alertStore.addError("Can't delete project", e.userMessage);
    },
  });
}

function resetErrors() {
  createProjectReset();
  updateProjectReset();
}

function goBackToDashboard() {
  if (isEditMode.value && projectId.value) {
    router.push(`/projects/${projectId.value}`);
  } else {
    router.push("/projects");
  }
}

function showConvertToProductModal() {
  showConvertModal.value = true;
}

function onProjectConverted(_productId: string) {
  // Cache invalidated automatically by useConvertProjectToProduct mutation
  loadProject();
}

// Copy query data into local editable ref when it resolves
watch(project, (p) => { if (p) loadProject(); }, { immediate: true });

onMounted(() => {
  if (isNewMode.value) {
    // Seed publisherProductId from the ?productId= query so the dropdown preselects whatever
    // product the user was filtering by on the dashboard. Falsy/missing means no preselection.
    const raw = route.query.productId;
    const preselectedProductId = Array.isArray(raw) ? raw[0] : raw;
    // Use "" rather than undefined so the v-model matches the "No product" <option value="">
    // and renders it as the visibly selected default. The submit logic still treats "" as no
    // product (falsy check), so behaviour is unchanged.
    localProject.value = {
      ...({} as Project),
      publisherProductId: preselectedProductId || "",
    };
  }
});

watch(
  () => localProject.value.publisherProductId,
  (newProductId) => {
    if (
      newProductId &&
      isPublisherProject.value &&
      !localProject.value.selectedVersion
    ) {
      const activeVersion = availableVersions.value.find((v) => v.isActive);
      if (activeVersion) {
        localProject.value.selectedVersion = activeVersion.id;
      }
    } else if (!newProductId) {
      localProject.value.selectedVersion = "";
    }
  }
);

const breadcrumbs = computed(() => {
  const crumbs: Array<{ label: string; href?: string }> = [
    { label: 'Dashboard', href: '/' },
    { label: 'Projects', href: '/projects' }
  ];

  if (isEditMode.value && localProject.value.id) {
    crumbs.push({ label: localProject.value.name, href: `/projects/${localProject.value.id}` });
  }

  crumbs.push({ label: isEditMode.value ? 'Edit' : 'New' });

  return crumbs;
});
</script>

<template>


    <!-- Project Form -->
    <PanelGrid :columns="1">
      <PanelCard>
        <!-- Skeleton: edit mode while loading -->
        <template v-if="isEditMode && loading">
          <SkeletonLoader type="header" />
          <SkeletonLoader type="form" :fields="4" class="mt-4" />
        </template>

        <!-- Form content -->
        <template v-else>
        <PageHeader
            :title="isEditMode ? 'Edit Project' : 'New Project'"
            :breadcrumbs="breadcrumbs"
        >
          <template #actions>
            <div v-if="isEditMode" class="dropdown">
              <button
                  class="bb-btn-secondary-cta dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-cy="title-menu"
                  title="More actions"
              >
                <i class="bi bi-three-dots"></i>
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li v-if="isProductlessProject">
                  <a
                      class="dropdown-item"
                      href="#"
                      @click.prevent="showConvertToProductModal"
                  >
                    <i class="bi bi-box-seam me-2"></i> Convert to Product
                  </a>
                </li>
                <li v-if="isProductlessProject">
                  <hr class="dropdown-divider" />
                </li>
                <li>
                  <a
                      class="dropdown-item"
                      :href="`/audit/?documentType=Project&documentId=${localProject.id}&populateLatest=true`"
                      target="_blank"
                  >
                    <i class="bi bi-book me-2"></i> Audit History
                  </a>
                </li>
                <li>
                  <a
                      class="dropdown-item"
                      :href="`/scheduler/${localProject.id}`"
                      target="_blank"
                  >
                    <i class="bi bi-calendar me-2"></i> Open Schedules
                  </a>
                </li>
              </ul>
            </div>
          </template>
        </PageHeader>

        <BlockUI :active="saving">

        <FormAlert :error="saveError" @dismiss="resetErrors" />

        <form>
          <!-- Project Name -->
          <div class="row mb-3">
            <label for="projectName" class="col-sm-3 col-form-label">
              Project Name <span class="text-danger">*</span>
            </label>
            <div class="col-sm-9">
              <input
                type="text"
                class="form-control"
                id="projectName"
                v-model="localProject.name"
                :class="
                  validation$.name.$errors.length > 0 ? 'is-invalid' : ''
                "
                required
                data-cy="project-name"
                placeholder="Enter project name"
              />
              <span
                v-for="error of validation$.name.$errors"
                :key="error.$uid"
              >
                <div class="invalid-feedback">{{ error.$message }}</div>
              </span>
            </div>
          </div>

          <!-- Product -->
          <div class="row mb-3">
            <label for="product" class="col-sm-3 col-form-label">
              Product
            </label>
            <div class="col-sm-9">
              <!-- Always show the dropdown. Even when there are no subscribed/owned products
                   there's still a meaningful selection available ("No product — start empty"),
                   which keeps the form layout consistent across orgs. -->
              <select
                class="form-select"
                id="product"
                v-model="selectedProductId"
                :disabled="isEditMode"
                :class="{ 'text-muted': isEditMode }"
                data-cy="project-product"
              >
                <option value="">No product — start with an empty project</option>
                <option
                  v-for="product in availableProducts"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.name }}
                </option>
              </select>

              <!-- New project + no subscribed products: subtle helper line under the dropdown
                   pointing the user toward how to get one. Doesn't block submission. -->
              <div
                v-if="!isEditMode && availableProducts.length === 0"
                class="form-text text-muted"
              >
                No subscribed products available. Contact Full Sail Partners to subscribe to a product.
              </div>

              <div
                v-if="isEditMode && isProductlessProject"
                class="d-flex align-items-center justify-content-between mt-2"
              >
                <div class="form-text text-muted">
                  <i class="bi bi-info-circle"></i> No product assigned yet
                </div>
                <button
                  type="button"
                  class="bb-btn-secondary-cta btn-sm"
                  @click="showConvertToProductModal"
                >
                  <i class="bi bi-box-seam me-1"></i>
                  Convert to Product
                </button>
              </div>
              <div v-else-if="isEditMode" class="form-text text-muted">
                <i class="bi bi-info-circle"></i> Product cannot be changed
                after project creation
              </div>
            </div>
          </div>

          <!-- Version (Publisher projects only) -->
          <div
            v-if="isPublisherProject && localProject.publisherProductId"
            class="row mb-3"
          >
            <label for="version" class="col-sm-3 col-form-label">
              Version
            </label>
            <div class="col-sm-9">
              <select
                class="form-select"
                id="version"
                v-model="localProject.selectedVersion"
                :disabled="!isPublisherProject"
                :class="{ 'text-muted': !isPublisherProject }"
                data-cy="project-version"
              >
                <option value="">Select a version</option>
                <option
                  v-for="version in availableVersions"
                  :key="version.id"
                  :value="version.id"
                >
                  v{{ version.version }} ({{ version.status
                  }}{{ version.isActive ? " - Current" : "" }})
                </option>
              </select>
              <div class="form-text mt-2">
                <span v-if="isPublisherProject">
                  Select which version to test/preview. Draft versions allow
                  testing before publishing.
                </span>
                <span v-else>
                  Version selection is only available for publisher
                  projects.
                </span>
              </div>
            </div>
          </div>

          <!-- Created (Edit mode only) -->
          <div v-if="isEditMode && localProject.createdAt" class="row mb-3">
            <label for="createdAt" class="col-sm-3 col-form-label">
              Created
            </label>
            <div class="col-sm-9">
              <input
                type="text"
                class="form-control-plaintext"
                id="createdAt"
                :value="new Date(localProject.createdAt).toLocaleString()"
                readonly
              />
            </div>
          </div>

          <!-- Last Updated (Edit mode only) -->
          <div v-if="isEditMode && localProject.updatedAt" class="row mb-3">
            <label for="updatedAt" class="col-sm-3 col-form-label">
              Last Updated
            </label>
            <div class="col-sm-9">
              <input
                type="text"
                class="form-control-plaintext"
                id="updatedAt"
                :value="new Date(localProject.updatedAt).toLocaleString()"
                readonly
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="row">
            <div class="col-sm-9 offset-sm-3">
              <div class="d-flex justify-content-end gap-2">
                <!-- Delete button (Edit mode only) -->
                <button
                  v-if="isEditMode"
                  type="button"
                  class="bb-btn-danger btn-sm"
                  @click="deleteProjectHandler"
                  data-cy="delete"
                >
                  <i class="bi bi-trash me-1"></i>Delete
                </button>

                <!-- Cancel button -->
                <button
                  type="button"
                  class="bb-btn-cancel"
                  @click="goBackToDashboard"
                >
                  Cancel
                </button>

                <!-- Save/Create button (primary) -->
                <button
                  v-if="isEditMode"
                  type="submit"
                  class="bb-btn-primary-cta"
                  @click.prevent="saveProject"
                  data-cy="save"
                >
                  Save Changes
                </button>
                <button
                  v-else
                  type="submit"
                  class="bb-btn-primary-cta"
                  @click.prevent="createNewProject"
                  data-cy="create"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </form>
        </BlockUI>
        </template>
      </PanelCard>
    </PanelGrid>

    <!-- Convert to Product Modal -->
    <ConvertToProductModal
      v-model:show="showConvertModal"
      :project="localProject"
      @converted="onProjectConverted"
    />

    <DeleteProjectModal
      :show="showDeleteModal"
      :project-name="localProject.name"
      :pending="deleteProjectPending"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />
</template>

<style lang="scss" scoped>
/* Disabled select styling */
.form-select:disabled {
  background-color: var(--bs-gray-100);
  opacity: 0.7;
  cursor: not-allowed;
}

.form-select.form-select:disabled.text-muted {
  color: var(--bs-secondary-color);
}
</style>
